import { supabase } from "@/integrations/supabase/client";
import { expandQuery } from "@/lib/searchSynonyms";

export type SearchResultType =
  | "recipe"
  | "blog"
  | "collection"
  | "product"
  | "event"
  | "press"
  | "category"
  | "page";

export interface SiteSearchResult {
  title: string;
  slug: string;
  type: SearchResultType;
  excerpt?: string;
  image?: string;
  rank: number;
}

export const TYPE_LABELS: Record<SearchResultType, string> = {
  recipe: "Recipe",
  blog: "Blog",
  collection: "Collection",
  product: "Shop",
  event: "Event",
  press: "Press",
  category: "Category",
  page: "Page",
};

export const GROUP_ORDER: SearchResultType[] = [
  "recipe",
  "collection",
  "category",
  "blog",
  "product",
  "event",
  "press",
  "page",
];

export const STATIC_PAGES: { title: string; slug: string; excerpt: string; keywords: string }[] = [
  { title: "Home", slug: "/", excerpt: "Recipes, cookbooks, and everyday kitchen chaos", keywords: "home homepage start" },
  { title: "About Me", slug: "/about", excerpt: "Meet Jessica Secrest, the voice behind Applesauce and ADHD", keywords: "about jessica jess secrest bio story applesauce adhd" },
  { title: "All Recipes", slug: "/recipes", excerpt: "Browse every recipe — breakfast, dinner, dessert, snacks, salads, seasonings", keywords: "recipes browse all food cooking" },
  { title: "Recipe Index", slug: "/recipe-index", excerpt: "Every recipe in one A-Z list", keywords: "index list a-z all recipes" },
  { title: "Shop My Kitchen", slug: "/shop", excerpt: "Kitchen tools, pantry staples, and merch I actually use", keywords: "shop store products amazon walmart merch tools kitchen gear" },
  { title: "Cookbooks", slug: "/cookbooks", excerpt: "Order the Of Course It's Good cookbook", keywords: "cookbook book buy order signed" },
  { title: "Blog", slug: "/blog", excerpt: "Stories, roundups, and everything else", keywords: "blog posts stories articles" },
  { title: "Events", slug: "/events", excerpt: "Upcoming classes, appearances, and meet-ups", keywords: "events tour classes appearances tickets" },
  { title: "Press", slug: "/press", excerpt: "Press features and media coverage", keywords: "press media news features interviews" },
  { title: "Brand Partnerships", slug: "/contact?tab=partnerships", excerpt: "Work with Jessica — collaborations and sponsorships", keywords: "partnership brand collab sponsor work with me" },
  { title: "Contact", slug: "/contact", excerpt: "Say hi, ask a question, or send a note", keywords: "contact email message question help" },
  { title: "Newsletter", slug: "/subscribe", excerpt: "Get new recipes in your inbox", keywords: "newsletter subscribe email signup freebie" },
  { title: "Recipe Requests", slug: "/recipe-request", excerpt: "Ask for a recipe you want to see", keywords: "request ask suggestion recipe idea" },
  { title: "Privacy Policy", slug: "/privacy-policy", excerpt: "Privacy policy and data practices", keywords: "privacy data policy cookies" },
  { title: "Terms of Service", slug: "/terms", excerpt: "Terms of service and usage agreement", keywords: "terms conditions legal" },
];

function matchStaticPages(query: string): SiteSearchResult[] {
  const words = query.toLowerCase().split(/\s+/).filter((w) => w.length >= 2);
  if (!words.length) return [];
  return STATIC_PAGES.flatMap((p) => {
    const haystack = `${p.title} ${p.excerpt} ${p.keywords}`.toLowerCase();
    const hits = words.filter((w) => haystack.includes(w)).length;
    if (!hits) return [];
    const titleHit = words.some((w) => p.title.toLowerCase().includes(w));
    return [{
      title: p.title,
      slug: p.slug,
      type: "page" as const,
      excerpt: p.excerpt,
      rank: (titleHit ? 45 : 12) + hits,
    }];
  });
}

/**
 * One search across recipes, blog posts, collections, categories, shop
 * products, events, press and static pages — typo tolerant and synonym aware.
 */
export async function searchSite(rawQuery: string, limit = 30): Promise<SiteSearchResult[]> {
  const q = rawQuery.trim();
  if (q.length < 2) return [];

  const expanded = expandQuery(q);
  const { data, error } = await supabase.rpc("search_site" as any, { q: expanded, lim: limit });
  if (error) console.error("search_site failed", error);

  const rows = ((data ?? []) as any[]).map((r) => ({
    title: r.title as string,
    slug: r.slug as string,
    type: (r.result_type as SearchResultType) ?? "page",
    excerpt: (r.excerpt as string) || undefined,
    image: (r.image_url as string) || undefined,
    rank: Number(r.rank) || 0,
  }));

  const combined = [...matchStaticPages(q), ...rows].sort((a, b) => b.rank - a.rank);

  const seen = new Set<string>();
  return combined.filter((item) => (seen.has(item.slug) ? false : (seen.add(item.slug), true))).slice(0, limit);
}

export function groupResults(results: SiteSearchResult[]) {
  const groups = new Map<SearchResultType, SiteSearchResult[]>();
  for (const r of results) {
    const list = groups.get(r.type) ?? [];
    list.push(r);
    groups.set(r.type, list);
  }
  return GROUP_ORDER.filter((t) => groups.has(t)).map((t) => ({ type: t, items: groups.get(t)! }));
}

/** Split text into matched / unmatched chunks so the UI can bold the hits. */
export function highlightParts(text: string, query: string): { text: string; match: boolean }[] {
  const words = Array.from(
    new Set(query.toLowerCase().split(/\s+/).filter((w) => w.length >= 2))
  ).sort((a, b) => b.length - a.length);
  if (!words.length) return [{ text, match: false }];

  const escaped = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "ig");
  return text
    .split(re)
    .filter((chunk) => chunk !== "")
    .map((chunk) => ({ text: chunk, match: words.includes(chunk.toLowerCase()) }));
}
